import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  Tabs,
  Tab,
  Chip,
  Button,
  Skeleton,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/system';
import {
  Search,
  CheckCircle,
  ArrowUpDown,
  CheckCircle2,
  Filter,
  Zap,
  Star,
  TrendingDown,
  AlertCircle
} from 'lucide-react';
import { motion } from 'framer-motion';

// Import services with the correct path
import { circleService } from '../../../../services/circleService';
import { carrierService } from '../../../../services/carrierService';

const PlanContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 0),
}));

const FilterBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  }
}));

const FilterControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    flexWrap: 'wrap',
  }
}));

const SearchField = styled(TextField)(({ theme }) => ({
  width: '240px',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  }
}));

const PlanCard = styled(Card)(({ theme, selected }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  height: '100%',
  border: `2px solid ${selected ? theme.palette.primary.main : '#e5e7eb'}`,
  borderRadius: theme.shape.borderRadius,
  boxShadow: selected ? '0 4px 6px rgba(255, 56, 92, 0.1)' : 'none',
  transform: selected ? 'scale(1.02)' : 'scale(1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.08)',
  }
}));

const CircleName = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  fontSize: '0.875rem',
  marginBottom: theme.spacing(0.5),
}));

const PlanName = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.25rem',
  marginBottom: theme.spacing(2),
}));

const PlanDetail = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
}));

const DetailValue = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: '0.875rem',
}));

const AvailabilityChip = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.success.main,
  fontSize: '0.875rem',
  marginTop: theme.spacing(1.5),
}));

const RecommendedBadge = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: 12,
  right: 12,
  backgroundColor: theme.palette.warning.main,
  color: '#fff',
  fontWeight: 600,
}));

const BestValueBadge = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: 12,
  right: 12,
  backgroundColor: theme.palette.success.main,
  color: '#fff',
  fontWeight: 600,
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(4),
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
}));

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  }
};

// Helper function to safely format numbers
const formatPrice = (value) => {
  // Check if value exists and is a number or can be converted to a number
  if (value !== undefined && value !== null) {
    const num = Number(value);
    if (!isNaN(num)) {
      return num.toFixed(2);
    }
  }
  return "0.00"; // Default fallback
};

// Helper function to safely get a numeric value
const getNumericValue = (value, defaultValue = 0) => {
  if (value !== undefined && value !== null) {
    const num = Number(value);
    return !isNaN(num) ? num : defaultValue;
  }
  return defaultValue;
};

const PlanSelection = ({ 
  selectedCarrier,
  onSelectPlan,
  selectedPlan,
  isLoading: parentIsLoading,
  setIsLoading: setParentIsLoading
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [circles, setCircles] = useState([]);
  const [filteredCircles, setFilteredCircles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [planTypeFilter, setPlanTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recommended');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  
  // Get plan types based on selected carrier
  const getPlanTypes = () => {
    if (selectedCarrier === 'Verizon') {
      return ['Welcome', 'Plus', 'Ultimate'];
    } else if (selectedCarrier === 'AT&T') {
      return ['Starter', 'Extra', 'Premium'];
    } else if (selectedCarrier === 'Tmobile') {
      return ['Essentials', 'Plus', 'Next'];
    } else if (selectedCarrier === 'Cricket') {
      return ['Core', 'More', 'Premium'];
    }
    return ['Basic', 'Standard', 'Premium'];
  };
  
  const planTypes = getPlanTypes();
  
  // Fetch circles from the API using services with improved error handling
  useEffect(() => {
    const fetchCircles = async () => {
      if (!selectedCarrier) return;
      
      setIsLoading(true);
      if (setParentIsLoading) setParentIsLoading(true);
      
      try {
        // Get the Auth0 token
        const token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'openid profile email'
        });
        
        // Use circleService to fetch all circles
        const response = await circleService.getCircles(token);
        
        // Check if we have a valid response
        if (!response.data || !Array.isArray(response.data)) {
          console.warn('API returned invalid circles data:', response.data);
          setCircles([]);
          setFilteredCircles([]);
          setIsLoading(false);
          if (setParentIsLoading) setParentIsLoading(false);
          return;
        }
        
        // Filter circles by carrier name - case insensitive comparison
        let filteredCircles = response.data;
        
        // If carrier filter is needed, apply it client-side since endpoint doesn't support it
        if (selectedCarrier) {
          filteredCircles = response.data.filter(circle => {
            // Add type checking before calling string methods
            // First, make sure carrier_name is a string
            const carrierNameMatch = typeof circle.carrier_name === 'string' && 
              circle.carrier_name.toLowerCase() === selectedCarrier.toLowerCase();
              
            // Next, check if plan_name is a string and contains the carrier name
            const planNameMatch = typeof circle.plan_name === 'string' && 
              circle.plan_name.toLowerCase().includes(selectedCarrier.toLowerCase());
              
            // Finally, check if carrier is a string
            const carrierMatch = typeof circle.carrier === 'string' && 
              circle.carrier.toLowerCase() === selectedCarrier.toLowerCase();
              
            // Return true if any of the conditions match
            return carrierNameMatch || planNameMatch || carrierMatch;
          });
        }
        
        // Process and normalize the data
        const processedCircles = filteredCircles.map(circle => ({
          ...circle,
          available_for_transfer: true, // Assume all circles are available for transfer
          monthly_payment_per_line: getNumericValue(circle.monthly_payment_per_line, 30),
          available_slots: getNumericValue(circle.available_slots, 1),
          family_size: getNumericValue(circle.family_size, 1),
          total_joiners: getNumericValue(circle.total_joiners, 0)
        }));
        
        // Filter out circles with 0 available slots
        const availableCircles = processedCircles.filter(circle => 
          getNumericValue(circle.available_slots) > 0
        );
        
        setCircles(availableCircles);
        setFilteredCircles(availableCircles);
      } catch (err) {
        console.error('Error fetching circles:', err);
        setError(err.friendlyMessage || 'Failed to load circles. Please try again.');
        
        // No mock data fallback - just set empty arrays when API fails
        setCircles([]);
        setFilteredCircles([]);
      } finally {
        setIsLoading(false);
        if (setParentIsLoading) setParentIsLoading(false);
      }
    };

    fetchCircles();
  }, [selectedCarrier, getAccessTokenSilently, setParentIsLoading]);
  
  // Apply filters
  useEffect(() => {
    let result = [...circles];
    
    // Filter by search term
    if (searchTerm) {
      result = result.filter(circle => {
        // Add type checking for search as well
        const nameMatch = typeof circle.name === 'string' && 
          circle.name.toLowerCase().includes(searchTerm.toLowerCase());
        const planNameMatch = typeof circle.plan_name === 'string' && 
          circle.plan_name.toLowerCase().includes(searchTerm.toLowerCase());
        return nameMatch || planNameMatch;
      });
    }
    
    // Filter by plan type
    if (planTypeFilter !== 'all') {
      result = result.filter(circle => {
        // Add type checking for plan_name
        return typeof circle.plan_name === 'string' && 
          circle.plan_name.toLowerCase().includes(planTypeFilter.toLowerCase());
      });
    }
    
    // Sort
    if (sortBy === 'price-low') {
      result.sort((a, b) => getNumericValue(a.monthly_payment_per_line) - getNumericValue(b.monthly_payment_per_line));
    } else if (sortBy === 'price-high') {
      result.sort((a, b) => getNumericValue(b.monthly_payment_per_line) - getNumericValue(a.monthly_payment_per_line));
    } else if (sortBy === 'available') {
      result.sort((a, b) => getNumericValue(b.available_slots) - getNumericValue(a.available_slots));
    } else if (sortBy === 'recommended') {
      // Sort by a combination of price and availability
      result.sort((a, b) => {
        // Calculate a score based on price and availability (lower price and more slots = better)
        const scoreA = (100 - getNumericValue(a.monthly_payment_per_line)) + (getNumericValue(a.available_slots) * 2);
        const scoreB = (100 - getNumericValue(b.monthly_payment_per_line)) + (getNumericValue(b.available_slots) * 2);
        return scoreB - scoreA;
      });
    }
    
    setFilteredCircles(result);
    setPage(1); // Reset to first page when filters change
  }, [circles, searchTerm, planTypeFilter, sortBy]);
  
  // Paginate
  const paginatedCircles = filteredCircles.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );
  
  const totalPages = Math.ceil(filteredCircles.length / itemsPerPage);
  
  const handlePageChange = (event, value) => {
    setPage(value);
    
    // Scroll to top of results
    const resultsElement = document.getElementById('plan-selection-results');
    if (resultsElement) {
      resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  // Determine if a circle should be marked as "Best Value" or "Recommended"
  const getBadgeType = (circle, index) => {
    // Best Value: Lowest price in the filtered results
    if (filteredCircles.length > 0) {
      const minPrice = Math.min(...filteredCircles.map(c => getNumericValue(c.monthly_payment_per_line)));
      if (getNumericValue(circle.monthly_payment_per_line) === minPrice) {
        return 'best-value';
      }
    }
    
    // Recommended: First circle when sorted by recommended or has most available slots
    if ((sortBy === 'recommended' && index === 0) || 
        (filteredCircles.length > 0 && 
         getNumericValue(circle.available_slots) === Math.max(...filteredCircles.map(c => getNumericValue(c.available_slots))))) {
      return 'recommended';
    }
    
    return null;
  };
  
  const renderCircleCard = (circle, index) => {
    const isSelected = selectedPlan?.id === circle.id;
    const badgeType = getBadgeType(circle, index);
    
    // Safely get numeric values with defaults
    const pricePerLine = getNumericValue(circle.monthly_payment_per_line, 30);
    const availableSlots = getNumericValue(circle.available_slots, 0);
    const familySize = getNumericValue(circle.family_size, 1);
    const totalJoiners = getNumericValue(circle.total_joiners, 0);
    const totalMembers = familySize + totalJoiners;
    
    return (
      <Grid item xs={12} md={6} lg={4} key={circle.id}>
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: index * 0.05 }}
        >
          <PlanCard 
            selected={isSelected}
            onClick={() => onSelectPlan({
              id: circle.id,
              name: circle.plan_name || `${selectedCarrier} Plan`,
              circleName: circle.name,
              circleId: circle.id,
              pricePerLine: pricePerLine
            })}
          >
            {badgeType === 'best-value' && (
              <BestValueBadge
                icon={<Zap size={14} />}
                label="Best Value"
                size="small"
              />
            )}
            {badgeType === 'recommended' && (
              <RecommendedBadge
                icon={<Star size={14} />}
                label="Recommended"
                size="small"
              />
            )}
            
            <CardContent sx={{ p: 3, position: 'relative' }}>
              <CircleName variant="caption">{circle.name || 'Circle'}</CircleName>
              <PlanName variant="h6">
                {circle.plan_name || `${selectedCarrier} Unlimited`}
              </PlanName>
              
              <Divider sx={{ my: 1.5 }} />
              
              <PlanDetail>
                <DetailLabel>Price per line:</DetailLabel>
                <DetailValue>${formatPrice(pricePerLine)}/mo</DetailValue>
              </PlanDetail>
              
              <PlanDetail>
                <DetailLabel>Available slots:</DetailLabel>
                <DetailValue>{availableSlots}</DetailValue>
              </PlanDetail>
              
              <PlanDetail>
                <DetailLabel>Total Members:</DetailLabel>
                <DetailValue>{totalMembers}</DetailValue>
              </PlanDetail>
              
              {/* If circle has savings info, show it */}
              {pricePerLine < 50 && (
                <Box sx={{ 
                  mt: 1.5, 
                  p: 1, 
                  bgcolor: 'success.light', 
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <TrendingDown size={16} color="#10b981" />
                  <Typography variant="body2" sx={{ color: 'success.dark', fontWeight: 500, fontSize: '0.75rem' }}>
                    Save ${(50 - pricePerLine).toFixed(2)}/mo per line
                  </Typography>
                </Box>
              )}
              
              <AvailabilityChip>
                <CheckCircle2 size={16} color="currentColor" />
                <span>Available for transfer</span>
              </AvailabilityChip>
            </CardContent>
          </PlanCard>
        </motion.div>
      </Grid>
    );
  };
  
  // Render loading skeletons
  const renderSkeletons = () => {
    return Array(3).fill(0).map((_, index) => (
      <Grid item xs={12} md={6} lg={4} key={`skeleton-${index}`}>
        <Card sx={{ height: '100%', p: 3 }}>
          <Skeleton variant="text" width="30%" height={24} />
          <Skeleton variant="text" width="70%" height={32} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={1} sx={{ mb: 2 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="20%" />
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="20%" />
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="20%" />
          </Box>
          
          <Skeleton variant="text" width="50%" sx={{ mt: 2 }} />
        </Card>
      </Grid>
    ));
  };
  
  // If there's an error, show error message
  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertCircle size={16} style={{ marginRight: '8px' }} />
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </Box>
    );
  }
  
  return (
    <PlanContainer>
      <Typography variant="h5" gutterBottom align="center" sx={{ mb: 2 }}>
        Select a Plan
      </Typography>
      
      <Typography variant="body1" gutterBottom align="center" sx={{ mb: 4, maxWidth: '650px', mx: 'auto' }}>
        Choose a plan from {selectedCarrier} that best fits your needs.
      </Typography>
      
      <FilterBar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', md: 'auto' } }}>
          <SearchField
            placeholder="Search circles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        <FilterControls>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={planTypeFilter}
              onChange={(e) => setPlanTypeFilter(e.target.value)}
              displayEmpty
              startAdornment={
                <InputAdornment position="start">
                  <Filter size={16} />
                </InputAdornment>
              }
            >
              <MenuItem value="all">All Plans</MenuItem>
              {planTypes.map((type) => (
                <MenuItem key={type} value={type.toLowerCase()}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              displayEmpty
              startAdornment={
                <InputAdornment position="start">
                  <ArrowUpDown size={16} />
                </InputAdornment>
              }
            >
              <MenuItem value="recommended">Recommended</MenuItem>
              <MenuItem value="price-low">Price: Low to High</MenuItem>
              <MenuItem value="price-high">Price: High to Low</MenuItem>
              <MenuItem value="available">Most Available Slots</MenuItem>
            </Select>
          </FormControl>
        </FilterControls>
      </FilterBar>
      
      {filteredCircles.length > 0 && !isLoading && (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Showing {paginatedCircles.length} of {filteredCircles.length} results
        </Typography>
      )}
      
      <Box id="plan-selection-results">
        <Grid container spacing={3}>
          {isLoading ? (
            renderSkeletons()
          ) : paginatedCircles.length > 0 ? (
            paginatedCircles.map((circle, index) => renderCircleCard(circle, index))
          ) : (
            <EmptyStateBox>
              <Typography variant="h6" gutterBottom>
                No circles found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {searchTerm || planTypeFilter !== 'all' ? 
                  'Try adjusting your filters or search terms.' : 
                  `We couldn't find any circles for ${selectedCarrier}.`}
              </Typography>
              {searchTerm || planTypeFilter !== 'all' ? (
                <Button 
                  variant="outlined" 
                  onClick={() => {
                    setSearchTerm('');
                    setPlanTypeFilter('all');
                  }}
                  sx={{ mt: 2 }}
                >
                  Clear Filters
                </Button>
              ) : (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="body2">
                    No circles are available for this carrier. You might want to:
                  </Typography>
                  <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                    <li>Try selecting a different carrier</li>
                    <li>Check back later as new circles become available</li>
                  </ul>
                </Box>
              )}
            </EmptyStateBox>
          )}
        </Grid>
      </Box>
      
      {totalPages > 1 && (
        <PaginationContainer>
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={handlePageChange}
            size={isMobile ? "small" : "medium"}
            color="primary"
            showFirstButton
            showLastButton
          />
        </PaginationContainer>
      )}
    </PlanContainer>
  );
};

export default PlanSelection;